import {
  idlePageSession,
  type BootstrapSnapshot,
  type FillSource,
  type PageSessionSnapshot,
  type SuperCopySnapshot,
  type TabContext,
  type StateStamp,
  type SidebarSnapshot
} from '@oysterjs/smartfill-contracts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { SmartFillBridgeClient } from './client';
import { displayError, type DisplayError } from './errors';
import { SnapshotOrder } from './snapshot-order';

type SnapshotField = keyof SidebarSnapshot;
type EventRevisions = Record<SnapshotField, number>;
type LatestEvents = Partial<SidebarSnapshot>;

export type BridgeBootstrapState =
  | { status: 'waiting' | 'connecting'; snapshot: null; error: null }
  | { status: 'error'; snapshot: null; error: DisplayError }
  | { status: 'ready'; snapshot: BootstrapSnapshot; error: null };

/** Snapshot fields a command result may paint, subject to the revision guard. */
export type GuardedSnapshotField = 'pageSession' | 'superCopy';

/**
 * Command results are painted only when no push for the same field arrived
 * after the request started: capture snapshotRevision(field) before the
 * request and pass it as sinceRevision. A push is the newer truth, so a
 * slower response must not repaint over it.
 */
export interface SidebarSessionValue {
  /** Private render identity, not a protocol version or an account identifier. */
  accountGeneration: number;
  client: SmartFillBridgeClient;
  snapshot: BootstrapSnapshot;
  refresh: () => void;
  getSnapshot: () => BootstrapSnapshot;
  snapshotRevision: (field: GuardedSnapshotField) => number;
  /** The worker owns selection; a newer push wins over this command's reply. */
  selectActiveBucket: (bucketId: string) => Promise<FillSource | null>;
  /**
   * Returns false when the result belongs to a tab other than the one the
   * panel shows; a stale result is still the caller's outcome (true) even
   * though nothing is repainted.
   */
  applyPageSession: (
    snapshot: PageSessionSnapshot,
    options?: { expectedTabContextId?: string | null; sinceRevision?: number }
  ) => boolean;
  /** Returns whether the snapshot was painted. */
  applySuperCopy: (snapshot: SuperCopySnapshot, options?: { sinceRevision?: number }) => boolean;
}

const SidebarSessionContext = createContext<SidebarSessionValue | null>(null);

export function useBridgeBootstrap(client: SmartFillBridgeClient | null) {
  const [state, setState] = useState<BridgeBootstrapState>(() =>
    client
      ? { status: 'connecting', snapshot: null, error: null }
      : { status: 'waiting', snapshot: null, error: null }
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const [accountGeneration, setAccountGeneration] = useState(0);
  const snapshotRef = useRef<BootstrapSnapshot | null>(null);
  const revisionsRef = useRef<EventRevisions>(emptyRevisions());
  const latestEventsRef = useRef<LatestEvents>({});
  const orderRef = useRef(new SnapshotOrder());
  const sourceSelectionRef = useRef(0);

  const commitSnapshot = useCallback((snapshot: BootstrapSnapshot) => {
    if (snapshotRef.current?.auth.status === 'signed-in' && snapshot.auth.status !== 'signed-in') {
      // Revoke local caches even when React batches checking → signed-in into
      // one render. A same-account token renewal never crosses this boundary.
      setAccountGeneration((generation) => generation + 1);
      sourceSelectionRef.current++;
      const cleared = {
        activeBucket: null,
        pageSession: idlePageSession(),
        superCopy: emptySuperCopy()
      };
      for (const field of ['activeBucket', 'pageSession', 'superCopy'] as const) {
        revisionsRef.current[field]++;
        Object.assign(latestEventsRef.current, { [field]: cleared[field] });
      }
      snapshot = { ...snapshot, ...cleared };
    }
    snapshotRef.current = snapshot;
    setState({ status: 'ready', snapshot, error: null });
  }, []);

  const patchSnapshot = useCallback(
    <K extends SnapshotField>(field: K, value: BootstrapSnapshot[K]) => {
      const current = snapshotRef.current;
      if (!current) return;
      commitSnapshot({ ...current, [field]: value });
    },
    [commitSnapshot]
  );

  const applyBootstrap = useCallback(
    (snapshot: BootstrapSnapshot) => {
      const stamps = snapshot.stateRevisions;
      if (!orderRef.current.beginBootstrap(stamps.epoch)) return;
      const merged = { ...snapshot };
      for (const field of snapshotFields) {
        const accept = orderRef.current.accept(field, {
          epoch: stamps.epoch,
          revision: stamps[field]
        });
        if (accept) {
          revisionsRef.current[field] += 1;
          Object.assign(latestEventsRef.current, { [field]: snapshot[field] });
        } else {
          const latest = latestEventsRef.current;
          if (Object.prototype.hasOwnProperty.call(latest, field))
            Object.assign(merged, { [field]: latest[field] });
          else if (snapshotRef.current)
            Object.assign(merged, { [field]: snapshotRef.current[field] });
        }
      }
      commitSnapshot(merged);
    },
    [commitSnapshot]
  );

  useEffect(() => {
    snapshotRef.current = null;
    revisionsRef.current = emptyRevisions();
    latestEventsRef.current = {};
    orderRef.current = new SnapshotOrder();
    setState(
      client
        ? { status: 'connecting', snapshot: null, error: null }
        : { status: 'waiting', snapshot: null, error: null }
    );
    if (!client) return;

    const remember = <K extends SnapshotField>(
      field: K,
      value: BootstrapSnapshot[K],
      stamp?: StateStamp
    ) => {
      if (!orderRef.current.accept(field, stamp)) return;
      revisionsRef.current[field] += 1;
      latestEventsRef.current[field] = value;
      patchSnapshot(field, value);
    };
    // The page session belongs to a page, not a title: a tab event that only
    // renames the tab (a single-page form advancing a step) keeps the review.
    const rememberTab = (value: BootstrapSnapshot['tab'], stamp?: StateStamp) => {
      const previous = latestEventsRef.current.tab ?? snapshotRef.current?.tab;
      if (previous && sameTabIdentity(previous, value)) {
        remember('tab', value, stamp);
        return;
      }
      if (!orderRef.current.accept('tab', stamp)) return;
      const clearedPageSession = idlePageSession();
      revisionsRef.current.tab += 1;
      revisionsRef.current.pageSession += 1;
      latestEventsRef.current.tab = value;
      latestEventsRef.current.pageSession = clearedPageSession;
      const current = snapshotRef.current;
      if (current) {
        commitSnapshot({ ...current, tab: value, pageSession: clearedPageSession });
      }
    };

    let unsubscribers: Array<() => void>;
    try {
      unsubscribers = [
        client.subscribe('bootstrap.updated', (snapshot) => applyBootstrap(snapshot)),
        client.subscribe('auth.updated', (value, stamp) => remember('auth', value, stamp)),
        client.subscribe('tab.updated', rememberTab),
        client.subscribe('activeBucket.updated', (value, stamp) =>
          remember('activeBucket', value, stamp)
        ),
        client.subscribe('pageSession.updated', (value, stamp) =>
          remember('pageSession', value, stamp)
        ),
        client.subscribe('superCopy.updated', (value, stamp) => remember('superCopy', value, stamp))
      ];
    } catch (error) {
      setState({
        status: 'error',
        snapshot: null,
        error: displayError(error, 'The Cara extension bridge disconnected.')
      });
      return;
    }

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [client, commitSnapshot, patchSnapshot, applyBootstrap]);

  useEffect(() => {
    if (!client) return;
    let disposed = false;
    if (!snapshotRef.current) {
      setState({ status: 'connecting', snapshot: null, error: null });
    }

    void client
      .request('bootstrap', {})
      .then((snapshot) => {
        if (disposed) return;
        applyBootstrap(snapshot);
      })
      .catch((error: unknown) => {
        if (disposed || snapshotRef.current) return;
        setState({
          status: 'error',
          snapshot: null,
          error: displayError(error, 'Smart Fill could not connect to the Cara extension.')
        });
      });

    return () => {
      disposed = true;
    };
  }, [client, refreshToken, applyBootstrap]);

  const refresh = useCallback(() => setRefreshToken((current) => current + 1), []);

  const selectActiveBucket = useCallback(
    async (bucketId: string) => {
      if (!client) throw new Error('The Cara extension bridge is unavailable.');
      const revisions = revisionsRef.current;
      const revision = revisions.activeBucket;
      const selection = ++sourceSelectionRef.current;
      const source = await client.request('activeBucket.set', { bucketId });
      // Selection can finish after a newer selection, rename/delete notification,
      // or bridge reconnect. View-local mutation replies are never source authority.
      if (
        revisionsRef.current === revisions &&
        revisions.activeBucket === revision &&
        sourceSelectionRef.current === selection
      ) {
        patchSnapshot('activeBucket', source);
      }
      return source;
    },
    [client, patchSnapshot]
  );

  const snapshotRevision = useCallback(
    (field: GuardedSnapshotField) => revisionsRef.current[field],
    []
  );

  const isStale = (field: GuardedSnapshotField, sinceRevision: number | undefined) =>
    sinceRevision !== undefined && revisionsRef.current[field] > sinceRevision;

  const applyPageSession = useCallback(
    (
      snapshot: PageSessionSnapshot,
      options: { expectedTabContextId?: string | null; sinceRevision?: number } = {}
    ) => {
      const current = snapshotRef.current;
      const { expectedTabContextId, sinceRevision } = options;
      if (
        !current ||
        (expectedTabContextId !== undefined && current.tab.contextId !== expectedTabContextId)
      ) {
        return false;
      }
      if (!isStale('pageSession', sinceRevision)) patchSnapshot('pageSession', snapshot);
      return true;
    },
    [patchSnapshot]
  );

  const applySuperCopy = useCallback(
    (snapshot: SuperCopySnapshot, options: { sinceRevision?: number } = {}) => {
      if (!snapshotRef.current || isStale('superCopy', options.sinceRevision)) return false;
      patchSnapshot('superCopy', snapshot);
      return true;
    },
    [patchSnapshot]
  );

  const session = useMemo<SidebarSessionValue | null>(() => {
    if (!client || state.status !== 'ready') return null;
    return {
      accountGeneration,
      client,
      snapshot: state.snapshot,
      refresh,
      getSnapshot: () => snapshotRef.current ?? state.snapshot,
      snapshotRevision,
      selectActiveBucket,
      applyPageSession,
      applySuperCopy
    };
  }, [
    client,
    accountGeneration,
    state,
    refresh,
    snapshotRevision,
    selectActiveBucket,
    applyPageSession,
    applySuperCopy
  ]);

  return { state, session, refresh };
}

export function SidebarSessionProvider({
  value,
  children
}: {
  value: SidebarSessionValue;
  children: ReactNode;
}) {
  return <SidebarSessionContext.Provider value={value}>{children}</SidebarSessionContext.Provider>;
}

export function useSidebarSession(): SidebarSessionValue {
  const value = useContext(SidebarSessionContext);
  if (!value) throw new Error('useSidebarSession must be used inside SidebarSessionProvider.');
  return value;
}

const snapshotFields: readonly SnapshotField[] = [
  'auth',
  'tab',
  'activeBucket',
  'pageSession',
  'superCopy'
];

function sameTabIdentity(left: TabContext, right: TabContext): boolean {
  return (
    left.status === right.status &&
    left.contextId === right.contextId &&
    left.origin === right.origin
  );
}

function emptyRevisions(): EventRevisions {
  return {
    auth: 0,
    tab: 0,
    activeBucket: 0,
    pageSession: 0,
    superCopy: 0
  };
}

function emptySuperCopy(): SuperCopySnapshot {
  return { phase: 'idle', completedDraftId: null, draft: null, operations: [], error: null };
}
